/**
 * Gamification (`gamification-core` kit) — tendly profile card.
 *
 * Derives player state (XP/level/streak/badges) from live local-first
 * `service_record`/`asset` tables, without a new sync column:
 * `computePlayerFromTendly` is a pure deterministic reducer → instant
 * feedback (works for guests without sync). Rewards logging
 * maintenance and creating assets, with care-day streak. Private
 * leagues by default; does NOT use costs.
 *
 * Respects `prefers-reduced-motion`: XP bar animates with `transition` which
 * global guard disables; no JS animations.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Flame, Sparkles, Trophy } from "@fleet/icons";
import {
  computePlayerFromTendly,
  getAllServiceRecordsQuery,
  getAssetsQuery,
  levelProgress,
  queryKeys,
  type ServiceRecordLite,
  STREAK_CARE,
  TENDLY_BADGES,
} from "@tendly/shared";
import { createPowerSyncQuery } from "@tendly/shared/solid";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";

/** Human title by badge id (reactive i18n). */
function badgeTitle(t: (d: ReturnType<typeof msg>) => string, id: string): string {
  switch (id) {
    case "first-service": {
      return t(msg`First maintenance`);
    }
    case "ten-services": {
      return t(msg`Caretaker`);
    }
    case "fifty-services": {
      return t(msg`Guardian`);
    }
    case "century-services": {
      return t(msg`Care master`);
    }
    case "first-asset": {
      return t(msg`First asset`);
    }
    case "collection": {
      return t(msg`Collection`);
    }
    case "week-streak": {
      return t(msg`Week on top`);
    }
    case "month-streak": {
      return t(msg`Spotless month`);
    }
    case "level-5": {
      return t(msg`Level 5`);
    }
    case "level-10": {
      return t(msg`Level 10`);
    }
    default: {
      return id;
    }
  }
}

interface GamificationSectionProps {
  userId: string;
}

export function GamificationSection(props: GamificationSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const services = createPowerSyncQuery<ServiceRecordLite>({
    queryKey: () => queryKeys.services.allByUser(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAllServiceRecordsQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const assets = createPowerSyncQuery({
    queryKey: () => queryKeys.assets.list(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAssetsQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const player = createMemo(
    () => computePlayerFromTendly(services.data ?? [], assets.data?.length ?? 0).state,
  );
  const progress = createMemo(() => levelProgress(player().xp));
  const streak = createMemo(() => player().streaks[STREAK_CARE]?.current ?? 0);
  const owned = createMemo(() => new Set(player().badges));
  const isPending = () => services.isPending || assets.isPending;

  const levelLabel = () => {
    const { level } = progress();
    return t(msg`Level ${level}`);
  };
  const xpLabel = () => {
    const { xp } = player();
    return t(msg`${xp} XP`);
  };
  const streakLabel = () => {
    const days = streak();
    return t(msg`${days} day streak`);
  };
  const toNextLabel = () => {
    const xp = progress().xpToNext;
    return t(msg`${xp} XP to next level`);
  };

  return (
    <Card data-testid="gamification-section">
      <CardHeader>
        <div class="flex items-center gap-2">
          <Sparkles class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Progress`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <Show when={!isPending()} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
          {/* Nivel + barra de XP */}
          <div class="space-y-2">
            <div class="flex items-end justify-between">
              <div class="flex items-center gap-2">
                <span
                  class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary"
                  data-testid="gamification-level"
                >
                  {progress().level}
                </span>
                <div class="flex flex-col">
                  <span class="text-sm font-semibold text-foreground">{levelLabel()}</span>
                  <span class="text-xs text-muted-foreground" data-testid="gamification-xp">
                    {xpLabel()}
                  </span>
                </div>
              </div>
              <Show when={streak() > 0}>
                <span
                  class="flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning"
                  data-testid="gamification-streak"
                >
                  <Flame class="h-3.5 w-3.5" />
                  {streakLabel()}
                </span>
              </Show>
            </div>
            <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full rounded-full bg-primary transition-[width] duration-500"
                style={{ width: `${Math.round(progress().ratio * 100)}%` }}
              />
            </div>
            <p class="text-right text-xs text-muted-foreground">{toNextLabel()}</p>
          </div>

          {/* Badges (obtenidos en color, pendientes en gris) */}
          <div>
            <p class="mb-3 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Trophy class="h-4 w-4 text-muted-foreground" />
              {t(msg`Achievements`)}
            </p>
            <div class="flex flex-wrap gap-2">
              <For each={TENDLY_BADGES}>
                {(badge) => {
                  const has = () => owned().has(badge.id);
                  return (
                    <Tooltip>
                      <TooltipTrigger
                        as="div"
                        class="flex h-11 w-11 items-center justify-center rounded-xl border transition-colors"
                        classList={{
                          "border-primary/30 bg-primary/10 text-primary": has(),
                          "border-dashed border-muted-foreground/20 bg-muted/40 text-muted-foreground/40":
                            !has(),
                        }}
                        data-testid={`badge-${badge.id}`}
                        data-earned={has() ? "true" : "false"}
                        aria-label={badgeTitle(t, badge.id)}
                      >
                        <Trophy class="h-5 w-5" />
                      </TooltipTrigger>
                      <TooltipContent>{badgeTitle(t, badge.id)}</TooltipContent>
                    </Tooltip>
                  );
                }}
              </For>
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
